import apiClient from "src/utils/apiClient";
import type {
  UseCaseConfig,
  UseCaseVersionSummary,
  WorkspaceTask,
  WorkspaceResult,
  PermissionsMember,
  RoleValue,
} from "src/components/pages/auditModule/shared/types";
import { ROLE, ROLE_BY_API, API_ROLE } from "src/components/pages/auditModule/shared/types";

//TODO: Replace placeholder endpoints with real backend routes once Audit V2 APIs are implemented.
export interface AuditModuleTasksResponse {
  tasks: Array<{
    id: string;
    use_case_id: string;
    status: string;
    assigned_to: string | null;
    priority: number;
    created_at: string;
    // auditors assigned to this record (Review Sampling tiers: 1-3)
    reviewer_count?: number;
    // sample review progress: Unreviewed | PartiallyReviewed | FullyReviewed
    review_status?: string;
  }>;
  // the range the tasks were read for (null for a record-ID search)
  start_date?: string | null;
  end_date?: string | null;
  // more tasks matched than the backend cap; only the newest were returned
  truncated?: boolean;
}

// GET /audit_queue/tasks filters: a date range (YYYY-MM-DD, inclusive), or a
// record-ID search that ignores the range.
export interface AuditModuleTasksParams {
  start_date?: string;
  end_date?: string;
  search?: string;
}

export interface AuditModuleResultsResponse {
  results: Array<{
    id: string;
    task_id: string;
    auditor_id: string;
    status: string;
    decisions: Record<string, { decision: string }>;
  }>;
}

export interface AuditModuleUseCaseConfigsResponse {
  configs: UseCaseConfig[];
}

export interface AuditModuleSingleConfigResponse {
  config: UseCaseConfig;
}

export interface AuditModuleWorkspaceTaskResponse {
  task: WorkspaceTask | null;
  config: UseCaseConfig | null;
  result: WorkspaceResult | null;
  allTasks: WorkspaceTask[];
}

export interface AuditModuleWorkspaceResultResponse extends WorkspaceResult {}

// Body of POST /vlm_audit/sampling/{preview,run} (backend SamplingConfig).
// startDate/endDate are inclusive UTC YYYY-MM-DD bounds on the inspection's completion time.
export interface SamplingRunConfig {
  useCaseId: string;
  startDate: string;
  endDate: string;
  // % (1-100) of the records in the range to import; 100 imports all of them.
  samplePercent: number;
  seed?: number;
}

// Backend SamplingRunResult. allocation is the drawn record count per site (siteCode -> count).
// On a preview, ingested/auditorsUpdated/skipped are what a submit would do (new records /
// existing samples with no auditors that would get them / the rest); failed and batchId are null.
export interface SamplingRunResult {
  population: number;
  sampleSize: number;
  allocation: Record<string, number>;
  ingested: number | null;
  failed: number | null;
  skipped: number | null;
  auditorsUpdated?: number | null;
  batchId: string | null;
  // submit only: new records assigned per auditor alias.
  assignedAuditorCounts?: Record<string, number>;
}

// Raw shape returned by GET /audit_access/me (backend get_my_permission).
// The role is the caller's designation in the DynamoDB permissions registry
// (an LDAP audit-admin resolves to "Admin" even without a registry row).
export interface MyAuditPermission {
  role: string | null; // "Admin" | "Program Owner" | "Auditor" | null
  canManageMembers: boolean;
}

// Raw shape returned by GET /audit_access/my_use_cases.
export interface MyAuditUseCases {
  isAdmin: boolean;
  // useCaseId -> per-use-case role ("Admin" = owns/configures it, "Auditor" = audits it)
  useCaseRoles: Record<string, string>;
}

// Raw shape returned by the audit_access members API (backend PermissionMemberView).
interface PermissionMemberView {
  alias: string;
  displayName: string | null;
  role: string; // "Admin" | "Program Owner" | "Auditor"
  authorizedUseCases: string[];
  elevated: boolean;
  assignableRoles?: string[];
}

function toPermissionsMember(v: PermissionMemberView): PermissionsMember {
  return {
    login: v.alias,
    name: v.displayName || v.alias,
    role: ROLE_BY_API[v.role] ?? ROLE.AUDITOR,
    assigned_use_case_ids: v.authorizedUseCases ?? [],
    elevated: v.elevated,
    assignable_roles: (v.assignableRoles ?? [])
      .map((r) => ROLE_BY_API[r])
      .filter((r): r is RoleValue => Boolean(r)),
  };
}

// ----------------------------  Export (Export & Reports)  ----------------------------

// Comma-separated ids / aliases; blank = everything the caller may export.
export interface AuditExportParams {
  use_case_ids?: string;
  reviewers?: string;
  sites?: string;
  start_date?: string;
  end_date?: string;
  status?: string;
}

export interface AuditExportOptions {
  use_cases: { id: string; name: string }[];
  reviewers: string[];
  sites: string[];
}

export interface AuditExportPreview {
  headers: string[];
  rows: Record<string, string>[];
  count: number;
  max_rows: number;
  // more rows match than the CSV holds (it keeps the newest max_rows)
  truncated: boolean;
}

class AuditModuleService {
  async getTasks(params: AuditModuleTasksParams = {}): Promise<AuditModuleTasksResponse> {
    const { data } = await apiClient.get("/audit_queue/tasks", { params });
    return data;
  }

  async getResults(taskIds: string[]): Promise<AuditModuleResultsResponse> {
    const { data } = await apiClient.post("/audit_queue/results", { task_ids: taskIds });
    return data;
  }

  // ----------------------------  Config Endpoints  ----------------------------

  async getUseCaseConfigs(): Promise<AuditModuleUseCaseConfigsResponse> {
    const { data } = await apiClient.get("/audit_config/use_cases");
    return data;
  }

  async getUseCaseConfig(useCaseId: string): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.get("/audit_config/use_cases/" + useCaseId);
    return data;
  }

  async createUseCaseConfig(config: UseCaseConfig): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.post("/audit_config/use_cases", config);
    return data;
  }

  async updateUseCaseConfig(config: UseCaseConfig): Promise<AuditModuleSingleConfigResponse> {
    const { data } = await apiClient.put("/audit_config/use_cases/" + config.id, config);
    return data;
  }

  // Every version of a use case, newest first.
  async getUseCaseVersions(useCaseId: string): Promise<UseCaseVersionSummary[]> {
    const { data } = await apiClient.get<{ versions: UseCaseVersionSummary[] }>(
      "/audit_config/use_cases/" + encodeURIComponent(useCaseId) + "/versions",
    );
    return data.versions;
  }

  // One saved version as a read-only config (Versions tab "View").
  async getUseCaseVersion(useCaseId: string, version: number): Promise<UseCaseConfig> {
    const { data } = await apiClient.get<AuditModuleSingleConfigResponse>(
      "/audit_config/use_cases/" + encodeURIComponent(useCaseId) + "/versions/" + version,
    );
    return data.config;
  }

  // Copy an old version into a new active version; returns the restored config.
  async rollbackUseCaseVersion(useCaseId: string, version: number): Promise<UseCaseConfig> {
    const { data } = await apiClient.post<AuditModuleSingleConfigResponse>(
      "/audit_config/use_cases/" +
        encodeURIComponent(useCaseId) +
        "/versions/" +
        version +
        "/rollback",
    );
    return data.config;
  }

  // ----------------------------  Sampling Endpoints  ----------------------------

  async previewSampling(config: SamplingRunConfig): Promise<SamplingRunResult> {
    const { data } = await apiClient.post<SamplingRunResult>("/vlm_audit/sampling/preview", config);
    return data;
  }

  async submitSampling(config: SamplingRunConfig): Promise<SamplingRunResult> {
    const { data } = await apiClient.post<SamplingRunResult>("/vlm_audit/sampling/run", config);
    return data;
  }

  // ----------------------------  Export Endpoints  ----------------------------

  async getExportOptions(): Promise<AuditExportOptions> {
    const { data } = await apiClient.get<AuditExportOptions>("/audit_export/options");
    return data;
  }

  async getExportPreview(params: AuditExportParams): Promise<AuditExportPreview> {
    const { data } = await apiClient.get<AuditExportPreview>("/audit_export/preview", { params });
    return data;
  }

  async downloadExportCsv(params: AuditExportParams): Promise<Blob> {
    const { data } = await apiClient.get<Blob>("/audit_export/csv", {
      params,
      responseType: "blob",
    });
    return data;
  }

  // ----------------------------  Permissions / Access Endpoints  ----------------------------

  async getMyPermission(): Promise<MyAuditPermission> {
    const { data } = await apiClient.get<MyAuditPermission>("/audit_access/me");
    return data;
  }

  async getMyUseCases(): Promise<MyAuditUseCases> {
    const { data } = await apiClient.get<MyAuditUseCases>("/audit_access/my_use_cases");
    return data;
  }

  async getPermissionMembers(): Promise<PermissionsMember[]> {
    const { data } = await apiClient.get<PermissionMemberView[]>("/audit_access/members");
    return data.map(toPermissionsMember);
  }

  async updateMemberRole(alias: string, role: RoleValue): Promise<PermissionsMember> {
    const { data } = await apiClient.put<PermissionMemberView>("/audit_access/members/" + alias, {
      role: API_ROLE[role],
    });
    return toPermissionsMember(data);
  }

  async setMemberUseCases(alias: string, useCaseIds: string[]): Promise<PermissionsMember> {
    const { data } = await apiClient.put<PermissionMemberView>(
      "/audit_access/members/" + alias + "/use_cases",
      { useCaseIds },
    );
    return toPermissionsMember(data);
  }

  // ----------------------------  Workspace Endpoints  ----------------------------

  // `range` is the queue's date range, so the task list (progress, Prev / Next) matches it.
  async getWorkspaceTask(
    taskId: string,
    range?: { start_date: string; end_date: string },
  ): Promise<AuditModuleWorkspaceTaskResponse> {
    const { data } = await apiClient.get("/audit_workspace/tasks/" + taskId, { params: range });
    return data;
  }

  async saveWorkspaceResult(
    result: WorkspaceResult,
    submit: boolean,
  ): Promise<AuditModuleWorkspaceResultResponse> {
    const { data } = await apiClient.post("/audit_workspace/results", { ...result, submit });
    return data;
  }
}

export default new AuditModuleService();
